import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext(null);

const EN_PREFIX = "/en";

export function getLanguageFromPath(pathname) {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`)
    ? "en"
    : "fr";
}

export function getFrenchPath(pathname) {
  if (pathname === EN_PREFIX) return "/";
  if (pathname.startsWith(`${EN_PREFIX}/`)) {
    return pathname.slice(EN_PREFIX.length) || "/";
  }
  return pathname;
}

export function getEnglishPath(pathname) {
  const frenchPath = getFrenchPath(pathname);

  if (frenchPath === "/") return "/en/";
  return `${EN_PREFIX}${frenchPath}`;
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const language = getLanguageFromPath(location.pathname);

  const switchLanguage = useCallback(
    (nextLanguage) => {
      if (nextLanguage === language) return;

      const nextPath =
        nextLanguage === "en"
          ? getEnglishPath(location.pathname)
          : getFrenchPath(location.pathname);

      navigate(`${nextPath}${location.search}${location.hash}`);
    },
    [language, location.pathname, location.search, location.hash, navigate]
  );

  const value = useMemo(
    () => ({
      language,
      isEnglish: language === "en",
      isFrench: language === "fr",
      switchLanguage,
    }),
    [language, switchLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return context;
}
